import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../../utils/ToastSettings";
import { CouponsService } from "../../../services/Financial/Coupons/CouponsServices";
import { CouponsModel } from "../../../models/Financial/Coupons/CouponsModel";

interface ContextProps {
  params: any;
  validateCoupon: any;
  setParams: Dispatch<SetStateAction<any>>;
  cupons: CouponsModel[];
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingModal: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handleValidate: (data: any) => Promise<boolean>;
}

export const CouponsContext = createContext<ContextProps>({} as ContextProps);

interface CouponsProviderProps {
  children: ReactNode;
}

export const CouponsProvider = ({ children }: CouponsProviderProps) => {
  const [params, setParams] = useState<any>(null);
  const [validateCoupon, setValidateCoupon] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [cupons, setCupons] = useState<CouponsModel[]>([]);

  const service = new CouponsService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/financial/coupons")) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setCupons(_Response?.data?.items);
      setPages(_Response?.data?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os planos do sistema.");
    }
  }, [params, location.pathname]);

  const handleFetch = async (data: any) => {
    try {
      setIsLoadingModal(true);
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      setIsLoadingModal(false);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar plano.");
      setIsLoadingModal(false);
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar o plano.",
      );
      return false;
    }
  };

  const handleValidate = async (data: any) => {
    try {
      setIsLoadingModal(true);
      const [_Response, _Error, _Code, _Errors] = await service.validate(data);
      setIsLoadingModal(false);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      setError("");
      setValidateCoupon(_Response?.data);
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar plano.");
      setIsLoadingModal(false);
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <CouponsContext.Provider
      value={{
        params,
        setParams,
        cupons,
        pages,
        setPages,
        isLoading,
        isLoadingModal,
        error,
        validateCoupon,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleValidate,
      }}
    >
      {children}
    </CouponsContext.Provider>
  );
};

export const useCoupons = () => useContext(CouponsContext);
